import { useFormContext } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import {
  Button,
  Checkbox,
  Grid,
  Input,
  Prose,
  Select,
  TextArea,
} from '@/atoms';

import type { FunctionComponent } from 'react';
import type { TypeContactFormStandardProps } from './types';

const PhoneInput = dynamic(() => import('@/atoms/phone-input'), {
  ssr: false,
});

/**
 * ContactFormStandart
 */
export const ContactFormStandard: FunctionComponent<
  TypeContactFormStandardProps
> = ({
  textPolicies,
  buildingsSelectValues,
  mapOfAccommodation,
  watchBuilding,
  hearUsSelect,
  defaultBuilding,
  defaultApartment,
  hearUsDefaultValue,
  setPhoneCountry,
}: TypeContactFormStandardProps) => {
  const { t, lang } = useTranslation('common');
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <div className="absolute left-0 top-0 -mt-8 ml-6 lg:ml-16">
        <Image
          src="/assets/img/logo/logo-brown.svg"
          alt="logo"
          width={68}
          height={68}
          className="object-contain"
        />
      </div>

      <Grid xs={1} md={1} lg={2} className="!gap-8">
        <Grid.Item colSpanLG={1}>
          <Input
            label={t('components.organisms.contactForm.nameLabel')}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            name="name"
            type="text"
            placeholder={t('components.organisms.contactForm.namePlaceholder')}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Input
            label={t('components.organisms.contactForm.lastNameLabel')}
            name="lastName"
            type="text"
            placeholder={t(
              'components.organisms.contactForm.lastNamePlaceholder'
            )}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Input
            label={t('components.organisms.contactForm.emailLabel')}
            name="email"
            type="email"
            placeholder={t('components.organisms.contactForm.emailPlaceholder')}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Input
            label={t('components.organisms.contactForm.emailConfirmLabel')}
            name="emailConfirm"
            type="email"
            placeholder={t('components.organisms.contactForm.emailPlaceholder')}
            requiredMessage={t(
              'components.organisms.contactForm.emailConfirmationRequiredMessage'
            )}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <PhoneInput
            label={t('components.organisms.contactForm.phoneLabel')}
            name="telephone"
            language={lang === 'es' ? 'es' : 'eng'}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            className={`h-full ${
              errors.telephone && 'text-[#ff0000] placeholder-[#ff0000]'
            }`}
            setPhoneCountry={setPhoneCountry}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Select
            label={t('components.organisms.contactForm.hearAboustUs')}
            name="hearUs"
            defaultValue={hearUsDefaultValue}
            {...hearUsSelect}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Select
            label={t('components.organisms.contactForm.buildingLabel')}
            name="building"
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            // eslint-disable-next-line
            // @ts-ignore
            elements={buildingsSelectValues}
            defaultValue={{
              label: defaultBuilding?.label,
              value: defaultBuilding?.id.toString(),
            }}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={1}>
          <Select
            label={t('components.organisms.contactForm.accommodationLabel')}
            name="accommodation"
            defaultValue={defaultApartment}
            elements={(
              mapOfAccommodation.get(parseInt(watchBuilding?.toString())) ?? []
            ).filter(
              (item, index, self) =>
                index === self.findIndex((t) => t.value === item.value)
            )}
          />
        </Grid.Item>

        <Grid.Item colSpanLG={2}>
          <TextArea
            label={t('components.organisms.contactForm.addMore')}
            rows={5}
            name="addMore"
            placeholder={t(
              'components.organisms.contactForm.addMorePlaceholder'
            )}
          />
        </Grid.Item>
      </Grid>

      <div className="flex w-full flex-col justify-around gap-3">
        <p className="mb-1 text-[9px] uppercase tracking-wider">
          {t('components.organisms.contactForm.obligatoryFields')}
        </p>

        <Checkbox
          text={t('components.organisms.contactForm.marketingOptIn')}
          name="marketingOptIn"
        />
        <Checkbox
          text={t('components.organisms.contactForm.marketingGreystar')}
          name="marketingGreystar"
        />
      </div>

      <Button
        type="submit"
        size="large"
        variant="primary"
        className="md:ml-auto md:max-w-max"
      >
        {t('components.organisms.contactForm.sendButton')}
      </Button>

      <Prose
        html={textPolicies}
        className="text-other--detail text-xs leading-4 !text-primary-200"
      />
    </>
  );
};

ContactFormStandard.displayName = 'ContactFormStandard';
